import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { 
  ArrowLeft, MapPin, Briefcase, Mail, Calendar, 
  Trophy, Building2, Users, Heart, Camera, 
  MessageSquare, UserPlus, Globe, ExternalLink,
  Award, TrendingUp, Star, Shield
} from 'lucide-react';
import { AZURE } from '../services/api';
import PageHeader from '../components/layout/PageHeader';

// Design System Constants
const THEME = {
  primary: '#111111',
  gold: '#b48c2c',
  bg: '#f8fafc',
  white: '#ffffff',
  text: '#1f2937',
  textLight: '#6b7280'
};

// Role Mapping (alinhado com PeopleDirectory)
const ROLE_CONFIG = {
  'athlete': { 
    icon: Trophy, 
    color: 'green', 
    label: 'Atleta',
    category: 'FIELD_POOL'
  },
  'coach': { 
    icon: Users, 
    color: 'blue', 
    label: 'Técnico & Treinador',
    category: 'FIELD_POOL'
  },
  'health': { 
    icon: Heart, 
    color: 'red', 
    label: 'Saúde & Performance',
    category: 'PERFORMANCE'
  },
  'management': { 
    icon: Briefcase, 
    color: 'purple', 
    label: 'Gestão & Operações',
    category: 'BUSINESS'
  },
  'data': { 
    icon: TrendingUp, 
    color: 'indigo', 
    label: 'Dados & Tecnologia',
    category: 'BUSINESS'
  },
  'media': { 
    icon: Camera, 
    color: 'pink', 
    label: 'Conteúdo & Mídia',
    category: 'MEDIA'
  },
  'official': { 
    icon: Shield, 
    color: 'yellow', 
    label: 'Arbitragem',
    category: 'FIELD_POOL'
  },
  'education': { 
    icon: Award, 
    color: 'teal', 
    label: 'Educação',
    category: 'BUSINESS'
  },
  'industry': { 
    icon: Building2, 
    color: 'orange', 
    label: 'Indústria',
    category: 'BUSINESS'
  },
  'community': { 
    icon: Users, 
    color: 'gray', 
    label: 'Comunidade Arena',
    category: 'BUSINESS'
  }
};

const PageShell = ({ children }) => (
  <div className="min-h-screen" style={{ backgroundColor: THEME.bg }}>
    <div className="max-w-6xl mx-auto px-4 lg:px-6 py-6">
      {children}
    </div>
  </div>
);

const CommunityUserDetailPage = () => {
  const { userId } = useParams();
  const navigate = useNavigate();
  
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchUserData = async () => {
      setLoading(true);
      setError(null);
      
      try {
        // Buscar todos os usuários do diretório (já que não temos endpoint específico por ID)
        const { data } = await AZURE.get('/users/directory', {
          params: { limit: 500 }
        });
        
        const users = data.users || [];
        
        // Extrair ID numérico do formato 'user-123'
        const numericId = userId.startsWith('user-') 
          ? parseInt(userId.replace('user-', ''))
          : parseInt(userId);
        
        const foundUser = users.find(u => u.user_id === numericId);
        
        if (!foundUser) {
          setError('Usuário não encontrado');
          return;
        }
        
        // Normalizar dados do usuário
        const roleConfig = ROLE_CONFIG[foundUser.primary_role] || ROLE_CONFIG['community'];
        
        setUser({
          ...foundUser,
          roleConfig,
          display_name: foundUser.display_name || foundUser.full_name || foundUser.email?.split('@')[0] || 'Usuário'
        });
        
      } catch (err) {
        console.error('Erro ao buscar usuário:', err);
        setError('Erro ao carregar dados do usuário');
      } finally {
        setLoading(false);
      }
    };

    if (userId) {
      fetchUserData();
    }
  }, [userId]);

  if (loading) {
    return (
      <PageShell>
        <div className="animate-pulse space-y-6">
          <div className="h-48 bg-white rounded-xl"></div>
          <div className="h-96 bg-white rounded-xl"></div>
        </div>
      </PageShell>
    );
  }

  if (error || !user) {
    return (
      <PageShell>
        <div className="bg-white rounded-xl p-12 text-center">
          <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <Users className="text-gray-400" size={32} />
          </div>
          <h2 className="text-xl font-bold text-gray-900 mb-2">Usuário não encontrado</h2>
          <p className="text-gray-500 mb-6">
            {error || 'Este perfil pode ter sido removido ou não existe.'}
          </p>
          <button
            onClick={() => navigate('/jogadores')}
            className="px-6 py-2 bg-gradient-to-r from-[#111111] to-[#374151] text-white rounded-lg font-bold hover:shadow-md transition-all"
          >
            Voltar para Pessoas
          </button>
        </div>
      </PageShell>
    );
  }

  const RoleIcon = user.roleConfig.icon;
  const roleColorClass = `text-${user.roleConfig.color}-600 bg-${user.roleConfig.color}-50`;

  return (
    <PageShell>
      {/* Header com navegação */}
      <div className="mb-6">
        <button
          onClick={() => navigate('/jogadores')}
          className="flex items-center gap-2 text-gray-600 hover:text-[#111111] font-medium mb-4"
        >
          <ArrowLeft size={20} />
          Voltar para Pessoas
        </button>
        
        <PageHeader
          title={user.display_name}
          subtitle={`${user.roleConfig.label}${user.role_subtype ? ` • ${user.role_subtype}` : ''}`}
        />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Coluna Esquerda - Info Principal */}
        <div className="lg:col-span-1 space-y-6">
          
          {/* Card de Perfil */}
          <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
            {/* Banner */}
            <div className="h-24 bg-gradient-to-r from-[#111111] to-[#374151]"></div>
            
            {/* Avatar */}
            <div className="px-6 pb-6 -mt-12 text-center">
              <div className="w-24 h-24 rounded-full border-4 border-white bg-white shadow-md overflow-hidden mx-auto">
                {user.avatar_url ? (
                  <img 
                    src={user.avatar_url} 
                    alt={user.display_name}
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <div className="w-full h-full bg-gray-100 flex items-center justify-center text-3xl font-bold text-gray-400">
                    {user.display_name.charAt(0).toUpperCase()}
                  </div>
                )}
              </div>
              
              {/* Nome e Role */}
              <h1 className="text-2xl font-extrabold text-gray-900 mt-4 mb-1">
                {user.display_name}
              </h1>
              
              <div className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-lg ${roleColorClass} mb-4`}>
                <RoleIcon size={16} />
                <span className="text-sm font-bold">
                  {user.roleConfig.label}
                </span>
              </div>
              
              {user.role_subtype && (
                <p className="text-sm text-gray-600 mb-4">{user.role_subtype}</p>
              )}
              
              {/* Badges */}
              {user.is_verified && (
                <div className="inline-flex items-center gap-1 px-2 py-1 rounded bg-blue-100 text-blue-700 text-xs font-bold mb-4">
                  <Shield size={14} />
                  VERIFICADO
                </div>
              )}
              
              {/* Botões de Ação */}
              <div className="flex gap-2 mt-4">
                <button className="flex-1 flex items-center justify-center gap-2 px-4 py-2 bg-gradient-to-r from-[#111111] to-[#374151] text-white rounded-lg font-bold hover:shadow-md transition-all">
                  <UserPlus size={16} />
                  Acompanhar
                </button>
                <button className="px-4 py-2 border border-gray-200 rounded-lg text-gray-600 hover:bg-gray-50 transition-all">
                  <MessageSquare size={16} />
                </button>
              </div>
            </div>
          </div>

          {/* Card de Informações */}
          <div className="bg-white rounded-xl border border-gray-200 p-6 space-y-4">
            <h3 className="font-bold text-gray-900 mb-4">Informações</h3>
            
            {user.email && (
              <div className="flex items-start gap-3">
                <Mail size={18} className="text-gray-400 mt-0.5" />
                <div>
                  <p className="text-sm font-medium text-gray-900">Email</p>
                  <a 
                    href={`mailto:${user.email}`}
                    className="text-sm text-[#111111] hover:underline break-all"
                  >
                    {user.email}
                  </a>
                </div>
              </div>
            )}
            
            {(user.city || user.country) && (
              <div className="flex items-start gap-3">
                <MapPin size={18} className="text-gray-400 mt-0.5" />
                <div>
                  <p className="text-sm font-medium text-gray-900">Localização</p>
                  <p className="text-sm text-gray-600">
                    {user.city && user.country 
                      ? `${user.city}, ${user.country}`
                      : user.country || user.city}
                  </p>
                </div>
              </div>
            )}
            
            {user.organization_name && (
              <div className="flex items-start gap-3">
                <Building2 size={18} className="text-gray-400 mt-0.5" />
                <div>
                  <p className="text-sm font-medium text-gray-900">Organização</p>
                  <p className="text-sm text-gray-600">{user.organization_name}</p>
                </div>
              </div>
            )}
            
            {user.primary_sport && (
              <div className="flex items-start gap-3">
                <Trophy size={18} className="text-gray-400 mt-0.5" />
                <div>
                  <p className="text-sm font-medium text-gray-900">Esporte Principal</p>
                  <p className="text-sm text-gray-600">{user.primary_sport}</p>
                </div>
              </div>
            )}
            
            {user.created_at && (
              <div className="flex items-start gap-3">
                <Calendar size={18} className="text-gray-400 mt-0.5" />
                <div>
                  <p className="text-sm font-medium text-gray-900">Membro desde</p>
                  <p className="text-sm text-gray-600">
                    {new Date(user.created_at).toLocaleDateString('pt-BR', {
                      year: 'numeric',
                      month: 'long'
                    })}
                  </p>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Coluna Direita - Atividades e Conexões */}
        <div className="lg:col-span-2 space-y-6">
          
          {/* Card de Categoria */}
          <div className="bg-white rounded-xl border border-gray-200 p-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-[#111111] to-[#374151] flex items-center justify-center">
                <Star size={20} className="text-white" />
              </div>
              <div>
                <h3 className="font-bold text-gray-900">Categoria Radar</h3>
                <p className="text-sm text-gray-500">Área de atuação no ecossistema</p>
              </div>
            </div>
            
            <div className="p-4 bg-gray-50 rounded-lg border border-gray-200">
              <p className="text-lg font-bold" style={{ color: THEME.primary }}>
                {user.roleConfig.category === 'FIELD_POOL' && '🏆 Campo & Piscina'}
                {user.roleConfig.category === 'PERFORMANCE' && '❤️ Performance'}
                {user.roleConfig.category === 'BUSINESS' && '💼 Business'}
                {user.roleConfig.category === 'MEDIA' && '📸 Mídia'}
              </p>
              <p className="text-sm text-gray-600 mt-1">
                {user.roleConfig.category === 'FIELD_POOL' && 'Atletas, técnicos e arbitragem'}
                {user.roleConfig.category === 'PERFORMANCE' && 'Saúde, preparação e fisiologia'}
                {user.roleConfig.category === 'BUSINESS' && 'Gestão, dados e educação'}
                {user.roleConfig.category === 'MEDIA' && 'Conteúdo e imprensa'}
              </p>
            </div>
          </div>

          {/* Placeholder - Atividades Recentes */}
          <div className="bg-white rounded-xl border border-gray-200 p-6">
            <h3 className="font-bold text-gray-900 mb-4">Atividades Recentes</h3>
            <div className="text-center py-12 text-gray-400">
              <TrendingUp size={48} className="mx-auto mb-3 text-gray-300" />
              <p className="text-sm">Em breve: posts, comentários e interações</p>
            </div>
          </div>

          {/* Placeholder - Conexões */}
          <div className="bg-white rounded-xl border border-gray-200 p-6">
            <h3 className="font-bold text-gray-900 mb-4">Conexões</h3>
            <div className="text-center py-12 text-gray-400">
              <Users size={48} className="mx-auto mb-3 text-gray-300" />
              <p className="text-sm">Em breve: rede de contatos e colaborações</p>
            </div>
          </div>
        </div>
      </div>
    </PageShell>
  );
};

export default CommunityUserDetailPage;
